import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

let toastId = 0;
let listeners = [];

const toastState = {
  toasts: []
};

// Deduplication: aynı mesajın kısa sürede tekrar eklenmesini önle
let lastToastKey = '';
let lastToastTime = 0;

const addToast = (toast) => {
  // Aynı type+message 500ms içinde tekrar geldiyse engelle (StrictMode / çift çağrı koruması)
  const dedupeKey = `${toast.type}_${toast.message}`;
  const now = Date.now();
  if (dedupeKey === lastToastKey && now - lastToastTime < 500) {
    return -1;
  }
  lastToastKey = dedupeKey;
  lastToastTime = now;

  const id = ++toastId;
  const newToast = {
    id,
    ...toast,
    createdAt: now
  };

  // replace modu: önceki toastları temizle, sadece yenisini göster
  if (toast.replace) {
    toastState.toasts.length = 0;
  }

  toastState.toasts.push(newToast);

  // Auto remove after duration (default 5s, errors 8s)
  const duration = toast.duration || (toast.type === 'error' ? 8000 : 5000);
  setTimeout(() => {
    removeToast(id);
  }, duration);

  // Notify all listeners
  listeners.forEach(listener => listener([...toastState.toasts]));

  return id;
};

const removeToast = (id) => {
  const index = toastState.toasts.findIndex(t => t.id === id);
  if (index > -1) {
    toastState.toasts.splice(index, 1);
    listeners.forEach(listener => listener([...toastState.toasts]));
  }
};

export const useToast = () => {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([...toastState.toasts]);

  // Subscribe to toast updates
  const subscribe = useCallback((callback) => {
    listeners.push(callback);
    return () => {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  // Subscribe on mount
  React.useEffect(() => {
    const unsubscribe = subscribe(setToasts);
    return unsubscribe;
  }, [subscribe]);

  const toast = {
    success: (message, title = t('common.success'), opts) => addToast({ type: 'success', title, message, ...opts }),
    error: (message, title = t('common.error'), opts) => addToast({ type: 'error', title, message, ...opts }),
    warning: (message, title = t('common.warning'), opts) => addToast({ type: 'warning', title, message, ...opts }),
    info: (message, title = t('common.info'), opts) => addToast({ type: 'info', title, message, ...opts }),
    remove: removeToast
  };

  return { toast, toasts };
};
